import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    companyName: 'EYA TECH AUTOMATION',
    machineLanguage: 'MACHINE LANGUAGE',
    systemsControl: 'SYSTEMS CONTROL',
    service: 'SERVICES',
    contact: 'CONTACT',
    about: 'ABOUT',
    copyright: '© 2026 EYA',
    allRightsReserved: 'ALL RIGHTS RESERVED',
    // Services List
    servicesList: [
      'Intermittent or unpredictable shutdowns',
      'Recurring issues that have never been truly resolved',
      'Malfunctions related to automation systems, drives, or servo motors',
      'Inconsistent behavior between machines, HMI, and processes',
      'Systems that have become unstable after modifications or updates',
      'Modification or addition of equipment or operating sequences',
      'Creation, optimization, or modification of operator interfaces (HMI)',
      'Analysis of control sequences and logic',
      'New projects or integration of new functions',
      'Generation of clear, actionable technical reports',
      'We listen'
    ],
    // Process Flow Steps
    processSteps: [
      { 
        number: 'I', 
        title: 'EYA', 
        description: 'AWAITING REQUEST' 
      },
      { 
        number: 'II', 
        title: 'Problem', 
        description: 'Bug, change, or new system' 
      },
      { 
        number: 'III', 
        title: 'Listening', 
        description: 'COLLECT THE FACTS' 
      },
      { 
        number: 'IV', 
        title: 'Analysis', 
        description: 'IDENTIFY THE ROOT CAUSE' 
      },
      { 
        number: 'V', 
        title: 'Correction', 
        description: '' 
      }
    ],
    // About Section
    aboutContent: {
      title: 'ABOUT',
      specialization: 'Primary specialization: Allen-Bradley PLC & HMI systems',
      paragraphs: [
        `EyaTech is an independent industrial consulting and technical support company, specialized in resolving complex, intermittent, or poorly understood issues that negatively impact the performance and reliability of production systems.`,
        `Our Purpose\nTo restore a lasting connection between your company's strategic objectives and on-the-ground realities.\nFor us, a system is only valuable when it remains reliable and efficient over time, despite its inherent imperfections.`,
        `Our Method: Listen, Question, Correct\nWe listen to operators and field teams — the first witnesses of system malfunctions.\nWe question machines, processes, and data, without bias.\nBy cross-referencing this knowledge, we identify the root cause of the issue in order to deliver a permanent correction, not a temporary workaround.`,
        `Remote Intervention\nWhen conditions allow, an initial analysis can be carried out remotely.\nThe client may provide a version of the code, along with photos, videos, and a precise description of the observed issue.\n\nThis approach allows us to quickly identify potential failure points and, in some cases, apply a targeted correction without the need for travel.\nWhen an on-site intervention is required, this preliminary work provides a significant head start, enabling a faster, more efficient, and better-targeted intervention upon arrival.`,
        `Area of Expertise\nEyaTech specializes in PLC and HMI programming for Allen-Bradley systems, with a strong focus on industrial reliability and performance.\nOur work notably includes:\n• Variable Frequency Drives (VFDs)\n• Servo motors and encoders\n• Data transfer and synchronization\n• Advanced diagnostics and system monitoring\n• Optimization and reliability improvement of automated systems`,
        `Industries Served\nEyaTech is located in the Laurentians region, Quebec, Canada.\nWe serve clients locally and internationally and can intervene anywhere in the world, remotely or on site, depending on the nature of the mandate.`,
        `Why Choose EyaTech?\nFor a relationship without intermediaries.\nAt EyaTech, your point of contact is also your expert. The person you speak with is the one who:\n• comes to your facility,\n• conducts the analysis and executes the project,\n• ensures long-term follow-up,\n• remains reachable in the event of a real emergency.\n\nWe do not sell off-the-shelf products, consulting hours, or vague promises.\nWe sell a definitive solution, tailored to the exact reason for your call.`,
        `Our Mission\nTo solve your problem once and for all, so you can focus on your core business.\n\nWe do not perform miracles.\nWe simply do our job.`
      ]
    }
  },
  fr: {
    companyName: 'EYA TECH AUTOMATISATION',
    machineLanguage: 'LANGAGE MACHINE',
    systemsControl: 'SYSTÈME DE CONTRÔLE',
    service: 'SERVICES',
    contact: 'CONTACT',
    about: 'À PROPOS',
    copyright: '© 2026 EYA',
    allRightsReserved: 'TOUS DROITS RÉSERVÉS',
    // Services List
    servicesList: [
      'Arrêts intermittents ou imprévisibles',
      'Problèmes récurrents jamais réellement corrigés',
      'Dysfonctionnements liés aux automatismes, variateurs ou servomoteurs',
      'Comportements incohérents entre machines, HMI et processus',
      'Systèmes devenus instables après des modifications ou mises à jour',
      'Modification ou ajout d\'équipements ou de séquences de fonctionnement',
      'Création, optimisation ou modification d\'interfaces opérateur (HMI)',
      'Analyse de séquences et de logique de contrôle',
      'Nouveaux projets ou intégration de nouvelles fonctions',
      'Génération de rapports techniques clairs et exploitables',
      'On vous écoute'
    ],
    // Process Flow Steps
    processSteps: [
      { 
        number: 'I', 
        title: 'EYA', 
        description: 'EN ATTENTE DE DEMANDE' 
      },
      { 
        number: 'II', 
        title: 'Problème', 
        description: 'Bogue, changement ou nouveau système' 
      },
      { 
        number: 'III', 
        title: 'Écoute', 
        description: 'COLLECTE DES FAITS' 
      },
      { 
        number: 'IV', 
        title: 'Analyse', 
        description: 'IDENTIFIER LA CAUSE PRINCIPALE' 
      },
      { 
        number: 'V', 
        title: 'Correction', 
        description: '' 
      }
    ],
    // About Section
    aboutContent: {
      title: 'À PROPOS',
      specialization: 'Spécialisation principale : Systèmes PLC & HMI Allen-Bradley',
      paragraphs: [
        `EyaTech est une entreprise indépendante de conseil et de support technique industriel, spécialisée dans la résolution de problèmes complexes, intermittents ou mal compris, qui pénalisent la performance et la fiabilité des systèmes de production.`,
        `Notre raison d'être\nRétablir un lien durable entre les objectifs stratégiques de votre entreprise et les réalités du terrain. Pour nous, un système n'est utile que lorsqu'il reste fiable et efficace dans la durée, malgré ses imperfections inhérentes.`,
        `Notre méthode : Écouter, Interroger, Corriger\nNous écoutons les opérateurs et les équipes terrain — les premiers témoins des dysfonctionnements.\nNous interrogeons les machines, les processus et les données, sans préjugé.\nEn croisant ces savoirs, nous identifions la cause racine du problème afin d'apporter une correction définitive, et non un contournement temporaire.`,
        `Intervention à distance\nLorsque la situation le permet, une première analyse peut être réalisée à distance. Le client peut transmettre une version du code, accompagnée de photos, de vidéos et d'une description précise du problème observé.\n\nCette approche permet d'identifier rapidement les pistes de défaillance et, dans certains cas, d'apporter une correction ciblée sans déplacement.\nLorsqu'une intervention sur site s'avère nécessaire, ce travail préalable constitue un pas d'avance significatif, permettant une intervention plus rapide, plus efficace et mieux ciblée dès l'arrivée sur le terrain.`,
        `Rayon d'expertise\nEyaTech se spécialise dans la programmation d'automates et d'interfaces opérateurs (HMI) Allen-Bradley, avec un focus sur la fiabilité et la performance des systèmes industriels.\nNos interventions couvrent notamment :\n• Variateurs de fréquence (VFD)\n• Servomoteurs et encodeurs\n• Transfert et synchronisation de données\n• Diagnostic et supervision avancée (loupe de contrôle)\n• Optimisation et fiabilisation des systèmes automatisés`,
        `Secteurs desservis\nEyaTech est située dans la région des Laurentides, Québec, Canada.\nNous desservons des clients localement et à l'international, et pouvons intervenir partout dans le monde, à distance ou sur site selon la nature du mandat.`,
        `Pourquoi choisir EyaTech ?\nPour une relation sans interface.\nChez EyaTech, votre interlocuteur est aussi votre expert. La personne avec qui vous échangez est celle qui :\n• se déplace dans votre usine,\n• conduit l'analyse et réalise le projet,\n• assure le suivi dans la durée,\n• reste joignable en cas d'urgence réelle.\n\nNous ne vendons ni produits sur étagère, ni heures de consultance, ni promesses vagues.\nNous vendons une solution définitive, adaptée à la raison précise de votre appel.`,
        `Notre mission\nRésoudre votre problème, une bonne fois pour toutes, afin que vous puissiez vous concentrer sur votre métier.\n\nNous ne faisons pas de miracles.\nNous faisons simplement notre travail.`
      ]
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  const t = (key) => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
